/**
 * Theme / level certificate, rendered in the browser from the certificate's frozen `snapshot`
 * (so renaming an operator later never rewrites history). A4 landscape, serif, with the
 * same guilloché rosette as the on-screen seal.
 */
import { rosetteCurves } from "../components/rosette";
import { ROMAN, cap, fmtDateLong, unitOf } from "../utils";

const INK = [38, 33, 71];
const GOLD = [168, 128, 38];
const GOLD_LIGHT = [219, 190, 120];
const VIOLET = [67, 24, 255];
const PAPER = [252, 250, 244];

const centered = (doc, text, y, size, style = "normal", color = INK, font = "times") => {
  doc.setFont(font, style);
  doc.setFontSize(size);
  doc.setTextColor(...color);
  doc.text(text, doc.internal.pageSize.getWidth() / 2, y, { align: "center" });
};

const drawRosette = (doc, level, cx, cy, radius, numeral = true) => {
  const scale = radius / 96;
  doc.setLineWidth(0.08);
  doc.setDrawColor(...GOLD);
  rosetteCurves(level, { cx, cy, scale, steps: 180 }).forEach((pts) => {
    const segs = pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]);
    doc.lines(segs, pts[0][0], pts[0][1], [1, 1], "S", true);
  });
  doc.setLineWidth(0.5);
  doc.circle(cx, cy, radius, "S");
  doc.setLineWidth(0.15);
  doc.circle(cx, cy, radius * 0.94, "S");
  doc.setFillColor(...PAPER);
  doc.setLineWidth(0.4);
  doc.circle(cx, cy, radius * (numeral ? 0.34 : 0.2), "FD");
  if (!numeral) return; // themes are not numbered: the rosette stays purely decorative
  doc.setFont("times", "bold");
  doc.setFontSize(radius * 1.05);
  doc.setTextColor(...INK);
  doc.text(ROMAN[level], cx, cy + radius * 0.14, { align: "center" });
};

// jsPDF is heavy and only needed on download: load it on demand
export const buildCertificate = async (cert) => {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const snap = cert.snapshot;

  doc.setFillColor(...PAPER);
  doc.rect(0, 0, W, H, "F");

  if (cert.preview) {
    // specimen: this certificate was never issued
    doc.setFont("helvetica", "bold");
    doc.setFontSize(96);
    doc.setTextColor(232, 228, 214);
    doc.text("APERÇU", W / 2, H / 2 + 20, { align: "center", angle: 28 });
  }

  // double frame
  doc.setDrawColor(...INK);
  doc.setLineWidth(0.9);
  doc.rect(10, 10, W - 20, H - 20, "S");
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.25);
  doc.rect(13, 13, W - 26, H - 26, "S");

  // corner marks
  [
    [13, 13],
    [W - 13, 13],
    [13, H - 13],
    [W - 13, H - 13],
  ].forEach(([x, y]) => {
    doc.setFillColor(...GOLD);
    doc.circle(x, y, 1.1, "F");
  });

  centered(doc, (snap.company_name || "").toUpperCase(), 30, 10, "bold", VIOLET, "helvetica");
  centered(doc, "CERTIFICAT D'ACCRÉDITATION", 46, 28, "bold", INK);
  doc.setDrawColor(...GOLD_LIGHT);
  doc.setLineWidth(0.4);
  doc.line(W / 2 - 40, 52, W / 2 + 40, 52);

  centered(doc, "Il est certifié que", 66, 13, "italic", INK);
  centered(doc, snap.operator_name, 83, 34, "bold", INK);
  const unit = unitOf({ unit: snap.unit });
  centered(
    doc,
    unit.theme ? "a validé avec succès le thème" : "a validé avec succès",
    96,
    13,
    "italic",
    INK
  );
  centered(
    doc,
    unit.theme
      ? snap.level_title || "Thème"
      : `${cap(unit.one)} ${unit.num(cert.level)} — ${snap.level_title}`,
    110,
    20,
    "bold",
    VIOLET
  );
  centered(doc, snap.program_name, 119, 11, "normal", INK);

  centered(
    doc,
    `par une moyenne de ${String(cert.score_avg).replace(
      ".",
      ","
    )} / 100 aux simulations d'appels vocaux de ce ${unit.one},`,
    131,
    11,
    "italic",
    INK
  );
  centered(doc, "et a été validé par son responsable.", 137, 11, "italic", INK);

  drawRosette(doc, cert.level, W / 2, 163, 22, !unit.theme);

  // signature blocks
  doc.setDrawColor(...INK);
  doc.setLineWidth(0.3);
  doc.line(32, 176, 92, 176);
  doc.line(W - 92, 176, W - 32, 176);
  doc.setFont("times", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  doc.text(snap.validated_by_name || "Responsable", 62, 182, { align: "center" });
  doc.text("Validé par", 62, 187, { align: "center" });
  doc.text(fmtDateLong(cert.issued_at), W - 62, 182, { align: "center" });
  doc.text("Délivré le", W - 62, 187, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120, 116, 150);
  doc.text(`N° ${cert.number}`, 16, H - 16);
  doc.text("Certificat généré par Audio — Formation", W - 16, H - 16, { align: "right" });
  return doc;
};

export const downloadCertificate = async (cert) => {
  const slug = (v) =>
    String(v || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const safe = cert.snapshot.operator_name.replace(/[^\p{L}\p{N}]+/gu, "-");
  const file = unitOf({ unit: cert.snapshot.unit }).theme
    ? `certificat-theme-${slug(cert.snapshot.level_title) || cert.level}-${safe}.pdf`
    : `certificat-niveau-${cert.level}-${safe}.pdf`;
  (await buildCertificate(cert)).save(file);
};

/** Object URL of the rendered PDF (for an <iframe>); the caller revokes it. */
export const certificateBlobUrl = async (cert) => (await buildCertificate(cert)).output("bloburl");
